import clsx from 'clsx';
import { useMemo } from 'react';
import { useRedirectableCampaign } from '~/features/product-details/components/ProductOverview/hooks/useRedirectableCampaign';
import { usePDPData } from '~/features/product-details/context';
import ChevronRight from '~/icons/chevron.svg';
import MixAndMatch from '~/icons/mix-and-match.svg';
import BuyAsSet from '~/icons/buy-as-set.svg';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { Typography } from '~/shared/components';
import styles from './ProductCampaignRedirect.module.scss';
import Link, { LinkProps } from 'next/link';

type Props = Omit<LinkProps, 'href'> & {
    className?: string;
};

export const ProductCampaignRedirect: React.FC<Props> = ({ className, ...rest }) => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { product } = usePDPData();

    const campaign = useRedirectableCampaign(product);

    const destination = useMemo(() => {
        const pages = frame?.settings?.pageReferences;

        switch (campaign?.type) {
            case 'mixandmatch':
                return {
                    icon: MixAndMatch,
                    text: translate('pdp.mixmatchMessage'),
                    url: pages?.mixAndMatchPage?.url,
                };

            case 'bundle':
                return {
                    icon: BuyAsSet,
                    text: translate('pdp.bundleMessage'),
                    url: pages?.bundlePage?.url,
                };

            case 'bastard':
                return {
                    icon: BuyAsSet,
                    text: translate('pdp.bastardMessage'),
                    url: pages?.bastardPage?.url,
                };
            default:
                return null;
        }
    }, [campaign?.type]);

    // A campaign without an addressable id flattens the price list but has nowhere to link.
    if (!campaign?.promotionId || !destination?.url) return null;

    const { icon: CampaignIcon } = destination;

    return (
        <Link
            className={clsx(styles.campaignRedirect, className)}
            href={`${destination.url}?campaign=${encodeURIComponent(campaign.promotionId)}`}
            {...rest}
        >
            <CampaignIcon className={styles.campaignRedirectIcon} />

            <Typography className={styles.campaignRedirectSplash} variant="paragraph-S">
                {product.c_splash}
            </Typography>

            <div className={styles.campaignRedirectAction}>
                <Typography className={styles.campaignRedirectMessage} variant="paragraph-S">
                    {destination.text}
                </Typography>

                <ChevronRight />
            </div>
        </Link>
    );
};
